import {
  ArrowCounterClockwiseIcon,
  CaretLeftIcon,
  CaretRightIcon,
  CheckIcon,
  LockSimpleIcon,
  SlidersHorizontalIcon,
} from '@phosphor-icons/react'
import { TFunction } from 'i18next'
import { ReactNode, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import { useGetDisplayColumn, useUpdateDisplayColumn } from '@/api/display-column'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { BorderLoader } from '@/components/ui/loaders/border-loader'
import { Modal } from '@/components/ui/modal'
import { Switch } from '@/components/ui/switch'
import { getColumnId, Table, TableColumn } from '@/components/ui/table'
import { Typography } from '@/components/ui/typography'
import { EMPTY_VALUE } from '@/constants'
import { toastFactory } from '@/factories'
import { DisplayColumnFields, DisplayFieldType } from '@/models/features/display-column.models'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'

import {
  DEFAULT_FIELDS_BY_TYPE,
  PERSON_FIELDS_BY_TYPE,
  PLACEHOLDER_PERSON_BY_TYPE,
} from '../person-column.constants'
import { Person, PersonColumnSettings, PersonFieldKey } from '../person-column.types'
import {
  areFieldsEqual,
  fieldAppliesTo,
  getFieldValue,
  getPersonName,
  isFieldEnabled,
  pickPreviewSamples,
  setFieldEnabled,
} from '../person-column.utils'
import { pickDisplayColumnFields } from '../use-person-column-settings'
import * as S from './column-data-settings-modal.styled'

type PersonColumnT = TFunction<['person-column', 'common']>

// Note: the design's side columns of the preview (Figma "Column data settings": a narrow ID - ours
// reads "ID number" - and a 155px neighbour); the configured column fills the rest.
const PREVIEW_IDENTIFIER_WIDTH = '12rem'
const PREVIEW_NEIGHBOUR_WIDTH = '15.5rem'

export interface ColumnDataSettingsModalProps<T> {
  onClose: () => void
  settings: PersonColumnSettings
  columnId: string
  columnTitle: string
  pageName: string
  // Note: the table's rendered columns and current page rows - the preview reuses both
  columns: TableColumn<T>[]
  rows: T[]
  getPerson: (row: T) => Person | null
  renderCell: (row: T, fields: DisplayColumnFields) => ReactNode
  // Note: renders a person outside any row - for the placeholder preview
  renderPerson: (person: Person, fields: DisplayColumnFields) => ReactNode
}

const getFieldCopy = (t: PersonColumnT, field: PersonFieldKey, fieldType: DisplayFieldType) => {
  const isMixed = fieldType === 'mixed'

  switch (field) {
    case 'avatar':
      return {
        hint: t('person-column:modal.fields.avatar.hint'),
        label: t('person-column:modal.fields.avatar.label'),
      }
    case 'name':
      return {
        hint:
          fieldType === 'staff'
            ? t('person-column:modal.fields.name.hintStaff')
            : t('person-column:modal.fields.name.hint'),
        label: t('person-column:modal.fields.name.label'),
      }
    case 'role':
      return {
        hint: isMixed ? t('person-column:modal.fields.role.hintMixed') : '',
        label: t('person-column:modal.fields.role.label'),
      }
    case 'apartment':
      return {
        hint: isMixed ? t('person-column:modal.fields.residentRowsOnly') : '',
        label: t('person-column:modal.fields.apartment.label'),
      }
    case 'buildingNumber':
      return {
        hint: isMixed ? t('person-column:modal.fields.residentRowsOnly') : '',
        label: t('person-column:modal.fields.buildingNumber.label'),
      }
    case 'street':
      return {
        hint: isMixed ? t('person-column:modal.fields.residentRowsOnly') : '',
        label: t('person-column:modal.fields.street.label'),
      }
    case 'email':
      return { hint: '', label: t('person-column:modal.fields.email.label') }
    case 'phone':
      return { hint: '', label: t('person-column:modal.fields.phone.label') }
  }
}

// Note: what the field would show for the current sample - its value, or why it shows nothing
const getSampleText = (t: PersonColumnT, field: PersonFieldKey, person: Person | undefined) => {
  if (!person || field === 'avatar') return { muted: false, text: '' }
  if (!fieldAppliesTo(field, person)) {
    return {
      muted: true,
      text:
        person.kind === 'guest'
          ? t('person-column:modal.sample.notApplicableGuest')
          : t('person-column:modal.sample.notApplicableStaff'),
    }
  }

  const value = getFieldValue(t, field, person)
  if (value === EMPTY_VALUE && field === 'phone') {
    return { muted: true, text: t('person-column:modal.sample.shownAsDash') }
  }
  if (!value) {
    return {
      muted: true,
      text: {
        guest: t('person-column:modal.sample.notProvidedGuest'),
        resident: t('person-column:modal.sample.notProvidedResident'),
        staff: t('person-column:modal.sample.notProvidedStaff'),
      }[person.kind],
    }
  }
  return { muted: false, text: value }
}

type EditorProps<T> = Omit<ColumnDataSettingsModalProps<T>, 'settings'> & {
  settings: PersonColumnSettings
  savedFields: DisplayColumnFields
  buildingId: number
  buildingName: string
}

const ColumnDataSettingsEditor = <T,>({
  onClose,
  settings,
  savedFields,
  buildingId,
  buildingName,
  columnId,
  columnTitle,
  pageName,
  columns,
  rows,
  getPerson,
  renderCell,
  renderPerson,
}: EditorProps<T>) => {
  const { t } = useTranslation(['person-column', 'common'])
  const { mutateAsync: updateDisplayColumn, isPending: isUpdating } = useUpdateDisplayColumn()
  // Note: after the settings are stored, the modal stays open (Save loading) until the table's
  // rows are refetched - their `<column>Display` is what the BE now shows for the new settings.
  const [isRefreshingRows, setIsRefreshingRows] = useState(false)
  const isSaving = isUpdating || isRefreshingRows
  const { dashboard, column, fieldType } = settings

  const [draft, setDraft] = useState(savedFields)
  const [sampleIndex, setSampleIndex] = useState(0)
  // Note: Save stays enabled (docs: form-validation) - pressing it with nothing changed shows an
  // error instead, which then follows the switches (gone once they differ from the saved state).
  // Only that press sets it: a real save updates the cached settings to the draft while the modal
  // closes, which would otherwise flash the error.
  const [isEmptySaveAttempted, setIsEmptySaveAttempted] = useState(false)
  const hasChanges = !areFieldsEqual(fieldType, draft, savedFields)

  const defaults = DEFAULT_FIELDS_BY_TYPE[fieldType]
  const fieldKeys = PERSON_FIELDS_BY_TYPE[fieldType]
  const shownCount = fieldKeys.filter((field) => isFieldEnabled(draft, field)).length

  // Note: up to 4 real people from this page, then the design's example person - real rows often
  // lack some details, so the example shows what every switch does for a person with all of them.
  // A page with nobody to show (e.g. only PMS work orders) previews just the example.
  const example: { person: Person; row?: T } = { person: PLACEHOLDER_PERSON_BY_TYPE[fieldType] }
  const samples: { person: Person; row?: T }[] = [
    ...pickPreviewSamples(t, rows, getPerson, fieldType),
    example,
  ]
  const sample = samples[sampleIndex % samples.length] ?? example
  const previewPerson = sample.person

  // Note: [identifier column, this column, its neighbour] of the real table, deduplicated - the
  // identifier is the table's first column (ID number, …) unless that's this column itself.
  const position = columns.findIndex((tableColumn) => getColumnId(tableColumn) === columnId)
  const neighbour = columns[position + 1] ?? columns[position - 1]
  // Note: the preview fits the modal like the design (no horizontal scroll) - the side columns
  // keep a fixed width and this column takes the rest, cutting off with the cell's hover tooltip.
  const configured: TableColumn<T> = {
    ...columns[position],
    render: (row) => renderCell(row, draft),
    width: 'auto',
  }
  const previewColumns = [position > 0 ? columns[0] : undefined, configured, neighbour]
    .filter((previewColumn): previewColumn is TableColumn<T> => !!previewColumn)
    .filter((previewColumn, index, list) => list.indexOf(previewColumn) === index)
    .map(
      ({ getMenuItems, getVisibilityAction, highlighted, sortKey, tooltip, ...previewColumn }) => ({
        ...previewColumn,
        // Note: matched by id - the rest spread above is a new object, never === the original
        width:
          getColumnId(previewColumn) === columnId
            ? configured.width
            : neighbour && getColumnId(previewColumn) === getColumnId(neighbour)
              ? PREVIEW_NEIGHBOUR_WIDTH
              : PREVIEW_IDENTIFIER_WIDTH,
      }),
    )
  const configuredPosition =
    previewColumns.findIndex((previewColumn) => getColumnId(previewColumn) === columnId) + 1
  const placeholderColumns: TableColumn<Person>[] = previewColumns.map((previewColumn) => ({
    id: getColumnId(previewColumn),
    render: (person) =>
      getColumnId(previewColumn) === columnId ? renderPerson(person, draft) : EMPTY_VALUE,
    title: previewColumn.title,
    width: previewColumn.width,
  }))

  const handleSave = async () => {
    if (isSaving) return
    setIsEmptySaveAttempted(!hasChanges)
    if (!hasChanges) return

    try {
      await updateDisplayColumn({
        ...pickDisplayColumnFields(draft),
        buildingId,
        column,
        dashboard,
      })
    } catch {
      toastFactory.error(t('person-column:modal.toastSaveError'))
      return
    }

    // Note: the settings are saved either way - a failed refetch shows up in the table's own
    // error state, not as a failed save.
    if (settings.refreshRows) {
      setIsRefreshingRows(true)
      await settings.refreshRows().catch(() => undefined)
      setIsRefreshingRows(false)
    }
    toastFactory.success(t('person-column:modal.toastSaveSuccess'))
    settings.onSaved()
    onClose()
  }

  const samplePersonName = getPersonName(sample.person) || EMPTY_VALUE
  const sampleLabel = !sample.row
    ? t('person-column:modal.sample.labelExample', { name: samplePersonName })
    : {
        guest: t('person-column:modal.sample.labelGuest', { name: samplePersonName }),
        resident: t('person-column:modal.sample.labelResident', { name: samplePersonName }),
        staff: t('person-column:modal.sample.labelStaff', { name: samplePersonName }),
      }[sample.person.kind]
  const samplePosition = (sampleIndex % samples.length) + 1

  const intro = {
    mixed: 'person-column:modal.intro.mixed',
    resident: 'person-column:modal.intro.resident',
    staff: 'person-column:modal.intro.staff',
  } as const
  const detailsTitle = {
    mixed: t('person-column:modal.details.titleMixed'),
    resident: t('person-column:modal.details.titleResident'),
    staff: t('person-column:modal.details.titleStaff'),
  }

  return (
    <Modal
      size="medium"
      // Note: the design's 700px modal (Figma "Column data settings"), between medium and large
      width="70rem"
      title={t('person-column:modal.title')}
      headerIcon={SlidersHorizontalIcon}
      footer={
        <>
          <S.FooterStart>
            <Button
              use="ghost"
              disabled={isSaving || areFieldsEqual(fieldType, draft, defaults)}
              onClick={() => setDraft(defaults)}
            >
              <ArrowCounterClockwiseIcon />
              {t('person-column:modal.resetToDefault')}
            </Button>
          </S.FooterStart>
          <Button use="secondary" disabled={isSaving} onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button use="primary" loading={isSaving} onClick={handleSave}>
            {t('common:actions.save')}
            <CheckIcon />
          </Button>
        </>
      }
      onClose={onClose}
    >
      <S.Body>
        <Typography as="p" size="text-medium" color="black600">
          <Trans
            t={t}
            i18nKey={intro[fieldType]}
            values={{ column: columnTitle, page: pageName }}
            components={{ bold: <strong /> }}
          />
        </Typography>

        <S.Section>
          <S.SectionHeader>
            <Typography as="span" size="text-small" weight="semibold" color="black800">
              {t('person-column:modal.preview.title')}
            </Typography>
            <S.SampleSwitcher>
              <Typography as="span" size="text-small" color="black600" breakLines={1}>
                {`${sampleLabel} · ${samplePosition}/${samples.length}`}
              </Typography>
              <Button
                onlyBox
                use="ghost"
                size="small"
                aria-label={t('person-column:modal.sample.previous')}
                disabled={samples.length < 2}
                onClick={() =>
                  setSampleIndex((samplePosition - 2 + samples.length) % samples.length)
                }
              >
                <CaretLeftIcon />
              </Button>
              <Button
                onlyBox
                use="ghost"
                size="small"
                aria-label={t('person-column:modal.sample.next')}
                disabled={samples.length < 2}
                onClick={() => setSampleIndex(samplePosition % samples.length)}
              >
                <CaretRightIcon />
              </Button>
            </S.SampleSwitcher>
          </S.SectionHeader>
          <S.Preview $columnPosition={configuredPosition}>
            {sample.row ? (
              <Table
                id="column-data-settings-preview"
                enablePinning={false}
                columns={previewColumns}
                data={[sample.row as T]}
                getRowKey={() => 'preview'}
              />
            ) : (
              <Table
                id="column-data-settings-preview"
                enablePinning={false}
                columns={placeholderColumns}
                data={[previewPerson]}
                getRowKey={() => 'preview'}
              />
            )}
          </S.Preview>
          <S.InfoBanner type="info" message={t('person-column:modal.preview.hoverNote')} />
        </S.Section>

        <S.Section>
          <S.SectionHeader>
            <Typography as="span" size="text-small" weight="semibold" color="black800">
              {detailsTitle[fieldType]}
            </Typography>
            <Typography as="span" size="text-small" color="black400">
              {t('person-column:modal.details.counter', {
                shown: shownCount,
                total: fieldKeys.length,
              })}
            </Typography>
          </S.SectionHeader>
          <S.FieldList>
            {fieldKeys.map((field) => {
              const { label, hint } = getFieldCopy(t, field, fieldType)
              const sampleText = getSampleText(t, field, previewPerson)
              const isLocked = field === 'name'
              const isOn = isFieldEnabled(draft, field)

              return (
                <S.FieldRow key={field}>
                  <S.FieldText>
                    <Typography as="span" size="text-small" weight="medium" color="black800">
                      {label}
                    </Typography>
                    {hint && <S.FieldDescription>{hint}</S.FieldDescription>}
                    {sampleText.text && (
                      <S.FieldDescription $muted={sampleText.muted}>
                        {sampleText.text}
                      </S.FieldDescription>
                    )}
                  </S.FieldText>
                  <Switch
                    size="small"
                    checked={isOn}
                    // Note: the name is fixed, not unavailable - read-only keeps it un-greyed
                    readOnly={isLocked}
                    disabled={!isLocked && isSaving}
                    aria-label={label}
                    inlineLabel={
                      isLocked ? (
                        <S.AlwaysOn>
                          <Icon icon={LockSimpleIcon} size="1.2rem" />
                          {t('person-column:modal.fields.alwaysOn')}
                        </S.AlwaysOn>
                      ) : isOn ? (
                        t('common:booleans.yes')
                      ) : (
                        t('common:booleans.no')
                      )
                    }
                    onChange={(event) =>
                      setDraft(setFieldEnabled(draft, field, event.target.checked))
                    }
                  />
                </S.FieldRow>
              )
            })}
          </S.FieldList>
        </S.Section>

        <S.InfoBanner
          type="info"
          message={t('person-column:modal.savedForBuilding', { building: buildingName })}
        />

        {isEmptySaveAttempted && !hasChanges && (
          <S.InfoBanner type="error" message={t('person-column:modal.noChanges')} />
        )}
      </S.Body>
    </Modal>
  )
}

export const ColumnDataSettingsModal = <T,>({
  settings,
  onClose,
  ...props
}: ColumnDataSettingsModalProps<T>) => {
  const { t } = useTranslation(['person-column', 'common'])
  const activeBuilding = useSelector(selectActiveBuilding)
  const { data, isError } = useGetDisplayColumn({
    buildingId: activeBuilding?.id,
    column: settings.column,
    dashboard: settings.dashboard,
  })

  if (data && activeBuilding) {
    return (
      <ColumnDataSettingsEditor
        {...props}
        settings={settings}
        savedFields={pickDisplayColumnFields(data)}
        buildingId={activeBuilding.id}
        buildingName={activeBuilding.name ?? activeBuilding.text}
        onClose={onClose}
      />
    )
  }

  // Note: no Save here - saving the defaults over a configuration we couldn't read would lose it
  return (
    <Modal
      size="medium"
      // Note: the design's 700px modal (Figma "Column data settings"), between medium and large
      width="70rem"
      title={t('person-column:modal.title')}
      headerIcon={SlidersHorizontalIcon}
      footer={
        isError ? (
          <Button use="secondary" onClick={onClose}>
            {t('common:actions.close')}
          </Button>
        ) : undefined
      }
      onClose={onClose}
    >
      {isError ? (
        <Alert
          type="error"
          title={t('person-column:modal.loadError.title')}
          message={t('person-column:modal.loadError.message')}
        />
      ) : (
        <S.Loader>
          <BorderLoader size="medium" />
        </S.Loader>
      )}
    </Modal>
  )
}
